import { AfterViewInit, Component, ElementRef, Input, OnDestroy, Renderer2 } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LoginTokenResponse } from '@shared/models';
import { AuthService } from '@core/auth';
import { ToastService } from '@shared/services';
import { TranslateService } from '@ngx-translate/core';
import { showSpinner } from '@shared/utils';

@Component({
  selector: 'arivo-verification-code-input',
  templateUrl: './arivo-verification-code-input.component.html',
  styleUrls: ['./arivo-verification-code-input.component.scss'],
})
export class ArivoVerificationCodeInputComponent implements AfterViewInit, OnDestroy {
  @Input() redirectUrl?: string;
  private unlisteners: (() => void)[] = [];
  private inputs: HTMLInputElement[] = [];

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private http: HttpClient,
    private authService: AuthService,
    private toastService: ToastService,
    private translateService: TranslateService,
  ) {}

  // Submits the verification code to the API
  submitCode(): void {
    const code = this.getCode();
    if (code.length !== this.inputs.length) {
      return;
    }

    this.http
      .post<LoginTokenResponse>('/api/user/auth/app/v1/auth/code/confirm', { code })
      .pipe(showSpinner())
      .subscribe({
        next: (response) => {
          if (!response.meta?.session_token) {
            this.showCodeConfirmationFailedToast();
            return;
          }
          this.authService.logIn(response.meta.session_token, this.redirectUrl);
        },
        error: () => {
          this.showCodeConfirmationFailedToast();
        },
      });
  }

  showCodeConfirmationFailedToast(): void {
    this.toastService.add({
      message: this.translateService.instant('LogIn.CodeConfirmationFailed'),
      type: 'error',
    });
  }

  ngAfterViewInit(): void {
    const form = this.elementRef.nativeElement.querySelector('form') as HTMLFormElement | null;
    if (!form) return;

    this.inputs = Array.from(form.querySelectorAll('input')) as HTMLInputElement[];

    const isAllowedChar = (char: string) => /^[0-9A-Z]$/.test(char);

    const getInputIndex = (input: HTMLInputElement): number => this.inputs.indexOf(input);

    const getNextInput = (input: HTMLInputElement): HTMLInputElement | null => {
      const index = getInputIndex(input);
      return index >= 0 && index < this.inputs.length - 1 ? this.inputs[index + 1] : null;
    };

    const getPrevInput = (input: HTMLInputElement): HTMLInputElement | null => {
      const index = getInputIndex(input);
      return index > 0 ? this.inputs[index - 1] : null;
    };

    // Validates character and auto-advances to next input
    const handleInput = (e: Event) => {
      const input = e.target as HTMLInputElement;
      if (!input) return;

      let char = (input.value ?? '').toUpperCase();
      if (char.length > 1) char = char.charAt(0);

      if (!char || !isAllowedChar(char)) {
        input.value = '';
        return;
      }

      input.value = char;

      const next = getNextInput(input);
      if (next && input.value) {
        setTimeout(() => {
          next.focus();
          if (next.value) next.select();
        }, 5);
      }
    };

    // Distributes pasted characters across all inputs
    const handlePaste = (e: ClipboardEvent) => {
      e.preventDefault();
      const chars = (e.clipboardData?.getData('text') ?? '')
        .replace(/[^0-9A-Za-z]/g, '')
        .toUpperCase()
        .split('');

      this.inputs.forEach((input, i) => {
        input.value = chars[i] ?? '';
      });

      const firstEmpty = this.inputs.find((input) => !input.value);
      const targetInput = firstEmpty ?? this.inputs[this.inputs.length - 1];
      targetInput?.focus();
    };

    // Clears current input or moves to previous
    const handleBackspace = (e: KeyboardEvent) => {
      const input = e.target as HTMLInputElement;
      if (!input) return;

      if (input.value) {
        e.preventDefault();
        input.value = '';
        return;
      }

      const prev = getPrevInput(input);
      if (prev) {
        e.preventDefault();
        prev.focus();
        prev.value = '';
      }
    };

    const handleArrowLeft = (e: KeyboardEvent) => {
      const prev = getPrevInput(e.target as HTMLInputElement);
      if (prev) {
        e.preventDefault();
        prev.focus();
      }
    };

    const handleArrowRight = (e: KeyboardEvent) => {
      const next = getNextInput(e.target as HTMLInputElement);
      if (next) {
        e.preventDefault();
        next.focus();
      }
    };

    // Form-level input event delegation
    this.unlisteners.push(this.renderer.listen(form, 'input', handleInput));

    // Paste event on all inputs (always fills from first input)
    this.inputs.forEach((input) => {
      this.unlisteners.push(this.renderer.listen(input, 'paste', handlePaste));
    });

    // Per-input event listeners
    this.inputs.forEach((input) => {
      this.unlisteners.push(
        this.renderer.listen(input, 'focus', (e: FocusEvent) => {
          setTimeout(() => (e.target as HTMLInputElement).select(), 0);
        }),
      );

      this.unlisteners.push(
        this.renderer.listen(input, 'keydown', (e: KeyboardEvent) => {
          switch (e.key) {
            case 'Backspace':
              handleBackspace(e);
              break;
            case 'ArrowLeft':
              handleArrowLeft(e);
              break;
            case 'ArrowRight':
              handleArrowRight(e);
              break;
            default:
              // Block non-alphanumeric characters
              if (e.key.length === 1 && !/^[0-9a-zA-Z]$/.test(e.key)) {
                e.preventDefault();
              }
          }
        }),
      );
    });
  }

  ngOnDestroy(): void {
    this.unlisteners.forEach((unlistener) => unlistener());
    this.unlisteners = [];
  }

  // Combines all input values into a single code string
  private getCode(): string {
    return this.inputs.map((input) => input.value).join('');
  }
}
